<template>
    <div>
        <h3 class="text-xl sm:text-2xl xl:text-3xl mb-6">Transfer your domain to us</h3>

        <div class="flex flex-wrap">
            <div class="w-full pb-8 md:w-2/3 md:pr-4">
                <!-- Lookup a domain's availability. -->
                <sau-domain-lookup :transfer="true" :searching="searching" :action="checkIfDomainCanBeTransferred" :error="error"></sau-domain-lookup>

                <!-- Is the searched domain available? -->
                <h3 v-if="available != null" class="my-8 text-xl sm:text-2xl xl:text-3xl">
                    <template v-if="isDomainAlreadyInCart">
                        {{ domain }} is in your cart for transfer <i class="ml-3 fas fa-check text-success"></i>
                    </template>
                    <template v-else-if="available">
                        {{ domain }} is available for transfer <i class="ml-3 fas fa-check text-success"></i>
                    </template>
                    <template v-else>
                        {{ domain }} is not yet registered <i class="ml-3 far fa-exclamation-circle text-success"></i>
                    </template>
                </h3>

                <div v-if="availableForPurchase" class="text-center">
                    <p>Would you like to claim this domain name?</p>
                    <p><button class="btn btn-success" @click.prevent="addDomainToOrder()">Yes, add it to my cart!</button></p>
                </div>


                <div v-if="available && ! isDomainAlreadyInCart" class="flex flex-wrap">
                    <form class="w-100">
                        <div class="form-group mb-4">
                            <label for="eppcode">Domain Authorisation / EPP Code <span class="sau-help sm ml-2 align-middle" v-b-tooltip.hover title="The EPP (or domain authorisation code) can be obtained from your current registrar">?</span></label>
                            <input id="eppcode" type="text" class="form-control" v-model="eppcode">
                        </div>

                        <div class="form-group mb-4">
                            <label for="registration-period">Registration Period</label>
                            <select id="registration-period" class="form-control pricing-select" v-model="registrationPeriod">
                                <option
                                    v-for="(option, index) in extensionPricing.pricing"
                                    :key="index"
                                    :value="option.registration_period"
                                >{{ option.display }} - {{ option.rrp | currency}} (Ex. GST)</option>
                            </select>
                        </div>

                        <button
                            type="submit"
                            class="btn d-block ml-auto"
                            :class="[ canProceedWithTransfer ? 'btn-success' : 'btn-light']"
                            @click.prevent="addTransferToOrder()"
                            :disabled="! canProceedWithTransfer">Add To Cart</button>
                    </form>
                </div>

                <sau-apiro-response v-if="unhandledError" type="error" class="mt-8">
                    <template slot="message">There was a problem with the domain lookup service</template>
                    <template slot="details">
                        <p>Please try again later or contact support if the issue persists.</p>
                    </template>
                </sau-apiro-response>
            </div>


            <sau-ordering-sidebar :data="data" :order="order" :processing="false"></sau-ordering-sidebar>
        </div>
    </div>
</template>

<script>
import SauDomainLookup from './DomainLookup';
import SauApiroResponse from "../utilities/SauApiroResponse";

export default {
    props: {
        data: Object,
        order: Object,
    },

    components: {
        'sau-domain-lookup': SauDomainLookup,
        'sau-apiro-response': SauApiroResponse,
    },

    inject: ['addOrderItemPromise', 'updateItemPromise'],

    data() {
        return {
            type: 'domain-transfer',
            domain: '',
            error: '',
            unhandledError: false,
            searching: false,
            available: null,
            priceList: null,
            eppcode: '',
            registrationPeriod: null,
            availableForPurchase: null,
        };
    },

    mounted() {
        this.priceList = this.data.config.domain.domaintransfer;
    },

    methods: {

        checkIfDomainCanBeTransferred(domain) {

            this.error = null;
            this.searching = true;
            this.unhandledError = false;

            this.postRequest('/api/domains/check-availability', {domain: domain})
                .then(response => {
                    this.domain = domain;
                    this.available = ! response.data.available;
                    this.availableForPurchase = response.data.available;

                })
                .catch(error => {
                    this.available = null;

                    switch (error.response.status) {
                        case 422: // Unprocessable Entity. (validation error)
                            this.error = error.response.data.errors.domain[0];
                            break;
                        default:
                            this.error = 'Something went wrong. Please try again.';
                            this.unhandledError = true;
                    }
                })
                .finally(() => {
                    this.searching = false;
                    this.eppcode = '';
                    if(! this.error) {
                        this.setDefaultRegistrationPeriod();
                    }
                });
        },

        addDomainToOrder() {

            const itemData = {
                domain: this.domain,
                registration_period: this.data.config.domain.domainregister
                                        .find(ext => ext.extension == this.getExtensionFromDomain(this.domain))
                                        .pricing[0].registration_period,
            }

            return this.addOrderItemPromise('domain', itemData)
                .then(response => {
                    // Set the item data (location, ru id)

                    this.updateItemPromise('domain', response.slug, itemData)
                        .then(response => {

                        });
                });
        },




        addTransferToOrder() {

            const itemData = {
                domain: this.domain,
                eppcode: this.eppcode,
                registration_period: this.registrationPeriod,
            };

            this.addOrderItemPromise('domain-transfer', itemData)
                .then(response => {
                    // Set the item data (location, ru id)

                    this.updateItemPromise('domain-transfer', response.slug, itemData)
                        .then(response => {
                            // Toggle the state (for disabling ui)
                        });
                });
        },

        getExtensionFromDomain(domain) {
            return domain.slice(domain.indexOf('.'))
        },

        setDefaultRegistrationPeriod() {
            const extension = this.priceList.find(option => option.extension === this.getExtensionFromDomain(this.domain));
            this.registrationPeriod = extension.pricing[0].registration_period; // Set the default registration period to the first price
        }

    },

    computed: {

        extensionPricing(){
            if(! this.priceList) return [];
            return this.priceList.find(option => option.extension == this.getExtensionFromDomain(this.domain))
        },

        canProceedWithTransfer() {
            return !! (this.registrationPeriod && this.eppcode); // !! casts result to bool
        },

        isDomainAlreadyInCart() {
            return !! this.order.items.find(item => item.type === 'domain-transfer' && item.domain === this.domain);
        }

    }
}
</script>

<style scoped="scoped" lang="scss">

    .suggestion {
        &:hover {
            box-shadow: 0 0 2px theme('colors.blue');
        }
        &.selected, &:hover {
            &, * {
                color: theme('colors.blue') !important;
                border-color: theme('colors.blue') !important;
            }
        }
    }

</style>
